//------------------------------------------------------------------------------------------------------------------------
import * as React from 'react';
import _ from 'lodash';
//------------------------------------------------------------------------------------------------------------------------
import './Tile.less';
import { PartialTileData } from '@UI/Widgets/Things';
import { IComponentSizeProp } from './Interfaces';
import { isDefined, IsNullOrWhiteSpace } from '@Utils/Micro';
import { getIconClassName } from './IconSelectorUtils';
//------------------------------------------------------------------------------------------------------------------------
/**
 * Available types for tile.
 *
 * @export
 * @enum {number}
 */
export enum TileType {
    /**
     * Doesn't contain links.
     */
    info,

    /**
     * Doesn't contain progress bar and content.
     */
    stat,

    /**
     * Doesn't contain progress bar.
     * Gives more flexibility in terms of layout.
     */
    custom
}
//------------------------------------------------------------------------------------------------------------------------
/**
 * Progress bar (only for info type).
 *
 * @export
 * @interface TileProgressBar
 */
export interface TileProgressBar {
    /**
     * Progress bar's percent.
     *
     * @type {number}
     * @memberof TileProgressBar
     */
    percent: number;

    /**
     * Description for progress bar.
     *
     * @type {string}
     * @memberof TileProgressBar
     */
    description?: string;

    /**
     * Progress bar's color (color name, RGB, HEX, HSL, RGBA, HSLA).
     *
     * @type {string}
     * @memberof TileProgressBar
     */
    color?: string;
}
//------------------------------------------------------------------------------------------------------------------------
/**
 * Links for sending port events (only for stat type).
 *
 * @export
 * @interface TileLink
 */
export interface TileLink {
    /**
     * Used for the localization.
     *
     * @type {string}
     * @memberof TileLink
     */
    key?: string;

    /**
     * Link's description.
     *
     * @type {string}
     * @memberof TileLink
     */
    label?: string;

    /**
     * Link's URL
     *
     * If URL is going to be external link
     *
     */
    url?: string;
}
//------------------------------------------------------------------------------------------------------------------------
/**
 * Tile's props.
 *
 * @export
 * @interface TileProps
 */
export interface TileProps extends IComponentSizeProp {
    /**
     * Tile's type (info or stat).
     *
     * @type {TileType}
     * @memberof TileProps
     */
    type: TileType;

    /**
     * Tile's color (color name, RGB, HEX, HSL, RGBA, HSLA).
     *
     * @type {string}
     * @memberof TileProps
     */
    color: string;

    /**
     * Tile's icon (Font Awesome).
     *
     * @type {string}
     * @memberof TileProps
     */
    icon: string;

    /**
     * Tile's icon color (color name, RGB, HEX, HSL, RGBA, HSLA).
     *
     * @type {string}
     * @memberof TileProps
     */
    iconColor: string;

    /**
     * Tile's icon size 
     *
     * @type {string}
     * @memberof TileProps
     */
    iconSize: number;

    /**
     * Tile's subject.
     *
     * @type {(string | number)}
     * @memberof TileProps
     */
    subject: string | number;

    /**
     * Tile's content (only for info type).
     *
     * @type {(string | number)}
     * @memberof TileProps
     */
    content?: string | number;


    /***
     * Content font size
     */
    subjectFontSize?: number;

    /**
     * Tile's stats.
     *
     * @type {(string | number)}
     * @memberof TileProps
     */
    stats: string | number;

    /***
     * Content font size
     */
    statsFontSize?: number;

    /**
     * Text's color (color name, RGB, HEX, HSL, RGBA, HSLA).
     *
     * @type {string}
     * @memberof TileProps
     */
    textColor: string;

    /**
     * Progress bar (only for info type).
     *
     * @type {TileProgressBar}
     * @memberof TileProps
     */
    progressBar?: TileProgressBar;

    /**
     * Links for sending port events (only for stat type).
     *
     * @type {TileLink[]}
     * @memberof TileProps
     */
    links?: TileLink[];

    /**
     * Callback when a link has been clicked.
     *
     * @memberof TileProps
     */
    onClickLink?: (index: number) => void;

    /**
     * Settings and Data that used in case of custom TILE ONLY.
     */
    customData?: {
        content?: string | number;
        stats?: string | number;
        subject?: string | number;
        iconText?: string;
        icon?: string;
    }; //todo Tile data
    customSettings?: PartialTileData; //todo Tile data

}
//------------------------------------------------------------------------------------------------------------------------
/**
 * React component class for tile.
 *
 * @export
 * @class Tile
 * @extends {React.Component<TileProps, {}>}
 */
export class Tile extends React.Component<TileProps, {}> {
    //--------------------------------------------------------------------------------------------------------------------
    /**
     * Creates an instance of Tile.
     * @param {TileProps} props
     * @memberof Tile
     */
    constructor(props: TileProps) {
        super(props);
    }
    //--------------------------------------------------------------------------------------------------------------------
    /**
     * Gets the JSX element for info type.
     *
     * @returns {JSX.Element}
     * @memberof Tile
     */
    getInfoElement(): JSX.Element {
        let iconWidth = 50;
        let iconFontSize = isDefined(this.props.iconSize) ? this.props.iconSize : 30;
        let height = this.props.height - 10;
        let subjectLineHeight = 22;
        let statLineHeight = 20;
        let progressHeight = 14;
        let contentLineHeight = 16;
        let maxHeight = subjectLineHeight + statLineHeight + progressHeight + contentLineHeight;
        if (maxHeight > height) {
            /* dynamic change size */
            subjectLineHeight = Math.floor((subjectLineHeight / maxHeight) * height);
            statLineHeight = Math.floor((statLineHeight / maxHeight) * height);
            progressHeight = Math.floor((progressHeight / maxHeight) * height);
            contentLineHeight = Math.floor((contentLineHeight / maxHeight) * height);
        }

        return (
            <div>
                {/* Icon */}
                <span
                    style={{
                        float: 'left',
                        height: this.props.height,
                        width: iconWidth,
                        maxWidth: 90,
                        textAlign: 'center',
                        fontSize: iconFontSize,
                        lineHeight: this.props.height + 'px',
                        backgroundColor: 'rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <i
                        className={getIconClassName(this.props.icon)}
                        style={{ color: this.props.iconColor }}
                    >
                    </i>
                </span>
                <div
                    style={{
                        padding: '5px 10px',
                        marginLeft: iconWidth
                    }}
                >
                    {/* Subject */}
                    <span
                        style={{
                            /*textTransform: 'uppercase',*/
                            display: 'block',
                            fontWeight: 'bold',
                            fontSize: subjectLineHeight - 4,
                            lineHeight: subjectLineHeight + 'px',
                        }}
                    >
                        {this.props.subject}
                    </span>
                    {/* Stats */}
                    <span
                        style={{
                            display: 'block',
                            fontWeight: 'bold',
                            fontSize: statLineHeight - 4,
                            lineHeight: statLineHeight + 'px',
                        }}
                    >
                        {this.props.stats}
                    </span>
                    {/* Progress */}
                    {!_.isEmpty(this.props.progressBar) && _.isNumber(this.props.progressBar.percent)
                        ?
                        <div>
                            <div
                                style={{
                                    margin: '5px 0',
                                    height: progressHeight - 10,
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                <div
                                    style={{
                                        width: this.props.progressBar.percent + '%',
                                        maxWidth: '100%',
                                        height: '100%',
                                        backgroundColor: this.props.textColor
                                    }}
                                >
                                </div>
                            </div>
                            <span>
                                {this.props.progressBar.description}
                            </span>
                        </div>
                        :
                        null
                    }
                    {/* Content */}
                    <div
                        style={{
                            display: 'block',
                            fontSize: contentLineHeight - 2,
                            lineHeight: contentLineHeight + 'px',
                        }}>
                        {this.props.content}
                    </div>
                </div>
            </div >
        );
    }
    //--------------------------------------------------------------------------------------------------------------------
    /**
     * Gets the JSX element for stat type.
     *
     * @returns {JSX.Element}
     * @memberof Tile
     */
    getStatElement(): JSX.Element {
        let iconWidth = 90;
        let iconFontSize = isDefined(this.props.iconSize) ? this.props.iconSize : 50;
        let height = this.props.height - 20;
        let linkHeight = 18;
        let statLineHeight = isDefined(this.props.statsFontSize) ? this.props.statsFontSize : 30;
        let subjectLineHeight = isDefined(this.props.subjectFontSize) ? this.props.subjectFontSize : 20;
        let numberOfLinks = (_.isEmpty(this.props.links)) ? 0 : this.props.links.length;
        let maxHeight = numberOfLinks * linkHeight + statLineHeight + subjectLineHeight;
        if (maxHeight > height) {
            /* dynamic change size */
            subjectLineHeight = Math.floor((subjectLineHeight / maxHeight) * height);
            statLineHeight = Math.floor((statLineHeight / maxHeight) * height);
            linkHeight = Math.floor((linkHeight / maxHeight) * height);
        }
        return (
            <div>
                <div
                    style={{
                        padding: '5px 10px',
                        marginRight: iconWidth
                    }}
                >
                    {/* Stats */}
                    <div
                        style={{
                            fontWeight: 'bold',
                            lineHeight: (statLineHeight + 2) + "px",
                            fontSize: statLineHeight
                        }}
                    >
                        {this.props.stats}
                    </div>
                    {/* Subject */}
                    <div style={{
                        lineHeight: (subjectLineHeight + 2) + "px",
                        fontSize: subjectLineHeight
                    }}>
                        {this.props.subject}
                    </div>
                </div>
                {/* Links */}
                {!_.isEmpty(this.props.links)
                    ?
                    _.map(this.props.links, (l: TileLink, i: number) => {
                        return (
                            <div
                                key={'link-' + i}
                                style={{
                                    position: 'relative',
                                    textAlign: 'center',
                                    padding: '3px 0',
                                    cursor: 'pointer',
                                    lineHeight: linkHeight + "px",
                                    fontSize: linkHeight - 3,
                                    width: this.props.width - iconWidth
                                }}
                                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                    if (_.isFunction(this.props.onClickLink))
                                        this.props.onClickLink(i);
                                }}
                                onMouseEnter={(event: React.MouseEvent<HTMLDivElement>) => {
                                    const element: HTMLDivElement = event.target as HTMLDivElement;
                                    element.style.color = 'white';
                                    element.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                                }}
                                onMouseLeave={(event: React.MouseEvent<HTMLDivElement>) => {
                                    const element: HTMLDivElement = event.target as HTMLDivElement;
                                    element.style.color = '';
                                    element.style.backgroundColor = '';
                                }}
                            >
                                {(!IsNullOrWhiteSpace(l.url)) ?
                                    <a href={l.url} rel="noopener noreferrer" target="_blank">{l.label}&nbsp;<i className='fa fa-arrow-circle-right'></i></a>
                                    :
                                    <div>{l.label}&nbsp;<i className='fa fa-arrow-circle-right'></i></div>
                                }
                            </div>
                        )
                    })
                    :
                    null
                }
                {/* Icon */}
                <div
                    style={{
                        position: 'absolute',
                        height: this.props.height,
                        top: 0,
                        right: 0,
                        width: iconWidth,
                        maxWidth: 120,
                        textAlign: 'center',
                        fontSize: iconFontSize,
                        lineHeight: this.props.height + 'px',
                        backgroundColor: 'rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <i
                        style={{ color: this.props.iconColor }}
                        className={getIconClassName(this.props.icon)}
                    >
                    </i>
                </div>
            </div >
        );
    }
    //--------------------------------------------------------------------------------------------------------------------
    /**
     * Gets the JSX element for stat type.
     *
     * @returns {JSX.Element}
     * @memberof Tile
     */
    getCustomElement(): JSX.Element {

        const {
            width,
            iconSize, //todo
            customData,
            customSettings
        } = this.props;

        const { stats, subject, content, iconText, icon } = customData;
        const {
            statsFontWeight,
            subjectFontWeight,
            statsFontSize,
            statsFontColor,
            subjectHorizontalAligment,
            subjectFontColor,
            subjectBackgroundColorAlpha,
            subjectFontSize,
            iconTextFontSize,
            iconBackgroundColorAlpha,
            iconColor,
            iconPosition,
            statsHorizontalAligment,
            contentHorizontalAligment,
            contentFontColor,
            iconTextPosition
        } = customSettings;

        let iconTextWidth = 120; //todo maybe take it from props
        let iconFontSize = isDefined(iconSize) ? iconSize : 12;
        let contentLineHeight = 16;
        let statLineHeight = isDefined(statsFontSize) ? statsFontSize : 30;
        let subjectLineHeight = isDefined(subjectFontSize) ? subjectFontSize : 20;

        let iconStyle: React.CSSProperties;

        (iconPosition == "left") ?
            iconStyle = {
                position: 'absolute',
                top: 0,
                left: 0,
                lineHeight: (statLineHeight + 2) + "px",
                height: this.props.height,
            } :
            (iconPosition == "right") ?
                iconStyle = {
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    lineHeight: (statLineHeight + 2) + "px",
                    height: this.props.height,
                } :
                (iconPosition == "top") ?
                    iconStyle = {
                        position: 'absolute',
                        top: 0,
                    }
                    :
                    iconStyle = {
                        position: 'absolute',
                        bottom: 0,
                    }

        let otherElementWidth = (iconPosition == "bottom" || iconPosition == "top") ? width : width - iconTextWidth //todo
        let marginRight = (iconPosition == "right") ? iconTextWidth : "";
        let marginLeft = (iconPosition == "left") ? iconTextWidth : "";

        return (
            <div /*style={{display:'flex'}}*/>
                <div
                    style={{
                        marginRight: marginRight,
                        marginLeft: marginLeft,
                        marginTop: iconPosition == "top" ? iconFontSize : ""
                    }}
                >
                    {/* Stats */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: isDefined(statsHorizontalAligment) ? statsHorizontalAligment : 'flex-start',//todo
                            width: otherElementWidth,
                            lineHeight: (statLineHeight + 2) + "px",
                            fontSize: statLineHeight,
                            fontWeight: statsFontWeight,
                            color: statsFontColor
                        }}
                    >
                        {stats}
                    </div>
                    {/* Subject */}
                    <div style={{
                        display: 'flex',
                        justifyContent: isDefined(subjectHorizontalAligment) ? subjectHorizontalAligment : 'flex-start',//todo
                        lineHeight: (subjectLineHeight + 2) + "px",
                        fontSize: subjectLineHeight,
                        fontWeight: subjectFontWeight,
                        width: otherElementWidth,
                        color: subjectFontColor, //todo
                        backgroundColor: `rgba(0, 0, 0, ${subjectBackgroundColorAlpha})`
                    }}>
                        {subject}
                    </div>
                    {/* Content */}
                    <div
                        style={{
                            //todo maybe add font color
                            display: 'flex',
                            justifyContent: contentHorizontalAligment,
                            fontSize: contentLineHeight - 2,
                            lineHeight: contentLineHeight + 'px',
                            width: otherElementWidth,
                            color: contentFontColor

                        }}>
                        {content}
                    </div>
                </div>

                {/* Icon */}
                <div
                    style={{
                        backgroundColor: `rgba(0, 0, 0, ${iconBackgroundColorAlpha})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        flexDirection: iconTextPosition == "top" ? 'column-reverse' : iconTextPosition == "bottom" ? 'column' : iconTextPosition == "left" ? 'row-reverse' : 'row',
                        width: iconPosition == "bottom" || iconPosition == "top" ? width : iconTextWidth,
                        ...iconStyle
                    }}
                >
                    {isDefined(iconText) ?
                        <div style={{ overflow: 'hidden', color: iconColor, fontSize: iconTextFontSize, maxWidth: 80, textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {iconText}
                        </div>
                        : null
                    }
                    <i
                        style={{ color: iconColor, fontSize: iconFontSize, padding: '0px 5px' }}
                        className={getIconClassName(icon)}
                    >
                    </i>
                </div>
            </div>
        );
    }
    //--------------------------------------------------------------------------------------------------------------------

    /**
     * Render component's view.
     *
     * @returns {(JSX.Element | null | false)}
     * @memberof Tile
     */
    render(): JSX.Element | null | false {
        return (
            <div className='tile-primitive'
                style={{
                    width: this.props.width,
                    height: this.props.height,
                    backgroundColor: this.props.color,
                    color: this.props.textColor,
                    position: 'relative'
                }}
            >
                {this.props.type == TileType.info
                    ? this.getInfoElement() :
                    this.props.type == TileType.stat
                        ? this.getStatElement() :
                        this.getCustomElement()
                }
            </div>
        );
    }
    //--------------------------------------------------------------------------------------------------------------------
}
//------------------------------------------------------------------------------------------------------------------------